import React, { useMemo } from 'react';
import { useApp, useThroughput } from '../contexts/AppContext';

// Realistic OLT chassis variants — Huawei MA5800-X17, ZTE C650, FiberHome AN6000-7
// Picked randomly per visit (synced with the reshuffle button via AppContext).
export const OLT_BRANDS = [
  {
    id: 'huawei',
    vendor: 'HUAWEI',
    model: 'MA5800-X17',
    color: '#dc2626',          // brand red
    chassisFill: '#0c0c0c',
    chassisStroke: 'rgba(255,255,255,0.14)',
    slots: 17,
    rows: 14,
    accentLabel: 'GPON · XGS-PON · 10G',
    serial: 'HW-X17-2129',
  },
  {
    id: 'zte',
    vendor: 'ZTE',
    model: 'ZXA10 C650',
    color: '#1d8fe1',          // brand blue
    chassisFill: '#0c1322',
    chassisStroke: 'rgba(120,180,255,0.18)',
    slots: 19,
    rows: 12,
    accentLabel: 'GPON · XG(S)-PON · 50G-PON',
    serial: 'ZTE-C650-7841',
  },
  {
    id: 'fiberhome',
    vendor: 'FiberHome',
    model: 'AN6000-7',
    color: '#f97316',          // brand orange
    chassisFill: '#0a0a0a',
    chassisStroke: 'rgba(255,180,120,0.16)',
    slots: 14,
    rows: 16,
    accentLabel: 'GPON · 10G-EPON · NG-PON2',
    serial: 'FH-AN6K-3322',
  },
];

export function pickRandomOlt() {
  return OLT_BRANDS[Math.floor(Math.random() * OLT_BRANDS.length)];
}

export default function OltVisual() {
  const { stats, theme, brand } = useApp();
  const throughput = useThroughput();
  const olts = stats?.olts_managed ?? 1247;
  const onus = stats?.onus_online ?? 145320;
  const uptime = stats?.uptime ?? 99.99;

  // sync the in-widget sparkline with the global throughput stream
  const traffic = throughput && throughput.length ? throughput[throughput.length - 1].dl : 420;
  const sparkline = (throughput || []).slice(-24).map((p) => (p.dl - 220) / 5.6); // 0..100ish

  const b = brand || OLT_BRANDS[0];

  // pre-compute port grid coordinates (centralized so we can target any port for animations)
  const grid = useMemo(() => {
    const colCount = b.slots;
    const rowCount = b.rows;
    const startX = 78;
    const endX = 562;
    const startY = 130;
    const endY = 332;
    const colW = (endX - startX) / colCount;
    const rowH = (endY - startY) / rowCount;
    const cells = [];
    for (let c = 0; c < colCount; c++) {
      for (let r = 0; r < rowCount; r++) {
        cells.push({
          x: startX + c * colW + colW / 2,
          y: startY + r * rowH + rowH / 2,
          col: c,
          row: r,
        });
      }
    }
    return { cells, startX, endX, startY, endY, colCount, rowCount, colW, rowH };
  }, [b]);

  // pick a handful of "active" ports that emit data beams outward
  const activePorts = useMemo(() => {
    const picks = [];
    for (let i = 0; i < 5; i++) {
      const cell = grid.cells[Math.floor(Math.random() * grid.cells.length)];
      // direction: shoot upper-right corner with offset
      const dx = 90 + Math.random() * 60;
      const dy = -60 - Math.random() * 80;
      picks.push({ ...cell, dx, dy, delay: i * 0.7, dur: 2.4 + Math.random() * 1.2 });
    }
    return picks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id]);

  return (
    <div className="relative">
      {/* outer glow halo */}
      <div
        className="absolute -inset-3 -z-10 blur-3xl opacity-60 pointer-events-none"
        style={{ background: theme.primaryGlow }}
      />

      {/* HUD floating widgets — top-left */}
      <div className="absolute -top-2 -left-2 z-20 flex flex-col gap-1.5 pointer-events-none">
        <div className="px-2.5 py-1.5 bg-black/80 border border-white/15 backdrop-blur-md text-[9px] uppercase tracking-widest flex items-center gap-2">
          <span
            className="inline-block w-1.5 h-1.5 pulse-dot"
            style={{ background: b.color }}
          />
          <span style={{ color: b.color }}>{b.vendor}</span>
          <span className="text-neutral-500">·</span>
          <span className="text-neutral-300 font-bold">{b.model}</span>
        </div>
        <div className="px-2.5 py-1.5 bg-black/80 border border-white/10 backdrop-blur-md text-[9px] uppercase tracking-widest text-neutral-500">
          {b.serial} · slot 03 · rack a-12
        </div>
      </div>

      {/* HUD — bottom-right (live throughput + sparkline) */}
      <div className="absolute -bottom-2 -right-2 z-20 px-3 py-2 bg-black/85 border border-white/15 backdrop-blur-md w-[210px]">
        <div className="text-[9px] uppercase tracking-widest text-neutral-500 flex justify-between">
          <span>Throughput · live</span>
          <span style={{ color: 'var(--theme-primary)' }}>Gbps</span>
        </div>
        <div className="flex items-end gap-2 mt-1">
          <div className="font-display font-black tracking-tight text-2xl leading-none" style={{ color: 'var(--theme-primary)' }}>
            {(traffic / 100).toFixed(2)}
          </div>
          <div className="flex items-end gap-[2px] h-7 mb-0.5">
            {sparkline.map((v, i) => (
              <div
                key={i}
                className="w-[3px] transition-all duration-300"
                style={{
                  height: `${v}%`,
                  background: 'var(--theme-primary)',
                  opacity: 0.3 + (i / sparkline.length) * 0.7,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* HUD — top-right corner (ONUs / uptime) */}
      <div className="absolute top-3 right-3 z-20 flex flex-col items-end gap-1 pointer-events-none">
        <div className="px-2.5 py-1.5 bg-black/80 border border-white/15 backdrop-blur-md">
          <div className="text-[8px] uppercase tracking-widest text-neutral-500">ONUs online</div>
          <div className="font-display font-bold text-base leading-none mt-0.5" style={{ color: 'var(--theme-primary)' }}>
            {onus.toLocaleString()}
          </div>
        </div>
        <div className="px-2.5 py-1 bg-black/80 border border-white/10 backdrop-blur-md text-[9px] uppercase tracking-widest text-emerald-400">
          ● uptime {uptime}%
        </div>
      </div>

      <div className="relative border border-white/10 bg-black/70 backdrop-blur-xl overflow-hidden">
        <svg
          viewBox="0 0 640 400"
          className="w-full block"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            // subtle 3D perspective like the product photos
            transform: 'perspective(1400px) rotateY(-4deg) rotateX(2deg)',
            transformOrigin: 'center center',
          }}
        >
          <defs>
            <pattern id="techGrid" width="16" height="16" patternUnits="userSpaceOnUse">
              <path d="M16 0H0V16" fill="none" stroke="rgba(255,255,255,0.045)" strokeWidth="0.5" />
            </pattern>

            <linearGradient id="chassisFront" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={b.chassisFill} stopOpacity="1" />
              <stop offset="50%" stopColor="#000" stopOpacity="0.6" />
              <stop offset="100%" stopColor={b.chassisFill} stopOpacity="1" />
            </linearGradient>

            <linearGradient id="metalHandle" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3a3a3a" />
              <stop offset="50%" stopColor="#1a1a1a" />
              <stop offset="100%" stopColor="#2a2a2a" />
            </linearGradient>

            <radialGradient id="ledGlow">
              <stop offset="0%" stopColor="#fff" stopOpacity="1" />
              <stop offset="50%" stopColor="var(--theme-primary)" stopOpacity="0.85" />
              <stop offset="100%" stopColor="var(--theme-primary)" stopOpacity="0" />
            </radialGradient>

            <linearGradient id="beamTrail" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--theme-primary)" stopOpacity="0" />
              <stop offset="80%" stopColor="var(--theme-primary)" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#fff" stopOpacity="1" />
            </linearGradient>

            <filter id="softBloom" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.5" result="b1" />
              <feMerge>
                <feMergeNode in="b1" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <filter id="hardBloom" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="3" result="b2" />
              <feMerge>
                <feMergeNode in="b2" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <clipPath id="chassisClip">
              <rect x="60" y="14" width="520" height="372" rx="2" />
            </clipPath>

            {/* beam paths for each activePort */}
            {activePorts.map((p, i) => (
              <path
                key={`bp-${b.id}-${i}`}
                id={`beam-${b.id}-${i}`}
                d={`M ${p.x} ${p.y} Q ${p.x + p.dx * 0.6} ${p.y + p.dy * 0.3}, ${p.x + p.dx} ${p.y + p.dy}`}
                fill="none"
              />
            ))}
          </defs>

          <rect width="640" height="400" fill="url(#techGrid)" />

          {/* CHASSIS body */}
          <g>
            {/* outer frame shadow */}
            <rect x="58" y="16" width="524" height="372" rx="2" fill="#000" opacity="0.6" />
            <rect x="60" y="14" width="520" height="372" rx="2" fill="url(#chassisFront)" stroke={b.chassisStroke} />
            {/* left rack ear w/ screws */}
            <rect x="60" y="14" width="14" height="372" fill="rgba(255,255,255,0.04)" stroke={b.chassisStroke} />
            <rect x="566" y="14" width="14" height="372" fill="rgba(255,255,255,0.04)" stroke={b.chassisStroke} />
            {[40, 100, 200, 300, 370].map((y, i) => (
              <g key={`screws-${i}`}>
                <circle cx="67" cy={y} r="1.4" fill="#1a1a1a" stroke="rgba(255,255,255,0.18)" strokeWidth="0.4" />
                <circle cx="573" cy={y} r="1.4" fill="#1a1a1a" stroke="rgba(255,255,255,0.18)" strokeWidth="0.4" />
              </g>
            ))}
          </g>

          {/* TOP CONTROL CARD / MPU — brand strip */}
          <g clipPath="url(#chassisClip)">
            <rect x="78" y="22" width="484" height="38" fill="#050505" stroke="rgba(255,255,255,0.08)" />
            {/* fine grille on left */}
            <g opacity="0.6">
              {Array.from({ length: 10 }).map((_, i) => (
                <line
                  key={`g1-${i}`}
                  x1={84 + i * 3}
                  y1={28}
                  x2={84 + i * 3}
                  y2={54}
                  stroke="rgba(255,255,255,0.08)"
                />
              ))}
            </g>

            {/* status LEDs */}
            <g transform="translate(120, 32)">
              {['PWR', 'ALM', 'SYNC', 'ACT', 'XGS'].map((lbl, i) => (
                <g key={lbl} transform={`translate(${i * 28}, 0)`}>
                  <circle
                    cx="4"
                    cy="0"
                    r="2.6"
                    fill={i === 1 ? '#facc15' : i === 0 ? '#22c55e' : 'var(--theme-primary)'}
                    filter="url(#softBloom)"
                  >
                    <animate
                      attributeName="opacity"
                      values={i === 1 ? '0.2;1;0.2' : '0.6;1;0.6'}
                      dur={`${1 + (i * 0.3)}s`}
                      repeatCount="indefinite"
                      begin={`${i * 0.2}s`}
                    />
                  </circle>
                  <text x="10" y="3" fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#737373" letterSpacing="1">
                    {lbl}
                  </text>
                </g>
              ))}
            </g>

            {/* mini console ports */}
            <g transform="translate(270, 26)">
              {Array.from({ length: 4 }).map((_, i) => (
                <rect
                  key={`cp-${i}`}
                  x={i * 12}
                  y={3}
                  width="9"
                  height="9"
                  rx="1"
                  fill="#020202"
                  stroke="rgba(255,255,255,0.12)"
                />
              ))}
              <text x="0" y="22" fontFamily="JetBrains Mono, monospace" fontSize="5.5" fill="#525252" letterSpacing="0.8">
                CONSOLE · MGMT · USB · AUX
              </text>
            </g>

            {/* brand wordmark — right side */}
            <g transform="translate(495, 44)" filter="url(#softBloom)">
              <text
                fontFamily="Cabinet Grotesk, sans-serif"
                fontSize="16"
                fontWeight="900"
                fill={b.color}
                letterSpacing="0.5"
                textAnchor="end"
              >
                {b.vendor}
              </text>
            </g>
            <text x="495" y="56" fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#737373" letterSpacing="1.2" textAnchor="end">
              {b.model} · {b.accentLabel}
            </text>

            {/* PSU / FAN row */}
            <rect x="78" y="64" width="484" height="22" fill="#080808" stroke="rgba(255,255,255,0.06)" />
            {/* PSU modules */}
            {[0, 1, 2, 3].map((i) => (
              <g key={`psu-${i}`} transform={`translate(${82 + i * 96}, 68)`}>
                <rect width="92" height="14" fill="#0a0a0a" stroke="rgba(255,255,255,0.1)" />
                <rect x="3" y="3" width="22" height="8" fill="#020202" stroke="rgba(255,255,255,0.08)" />
                <circle cx="34" cy="7" r="1.6" fill="#22c55e" filter="url(#softBloom)">
                  <animate attributeName="opacity" values="0.6;1;0.6" dur={`${1.4 + i * 0.3}s`} repeatCount="indefinite" />
                </circle>
                <text x="42" y="9.5" fontFamily="JetBrains Mono, monospace" fontSize="5.5" fill="#737373" letterSpacing="1">
                  PSU{i + 1} · 48VDC
                </text>
              </g>
            ))}

            {/* Alarm strip */}
            <rect x="78" y="90" width="484" height="14" fill="#020202" stroke="rgba(255,255,255,0.06)" />
            <text x="84" y="100" fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#525252" letterSpacing="2">
              ALARM
            </text>
            {/* alarm LEDs */}
            <g transform="translate(120, 97)">
              {[
                { c: '#dc2626', lbl: 'CRIT' },
                { c: '#f97316', lbl: 'MAJ' },
                { c: '#facc15', lbl: 'MIN' },
                { c: '#22c55e', lbl: 'OK' },
              ].map((a, i) => (
                <g key={a.lbl} transform={`translate(${i * 50}, 0)`}>
                  <circle cx="3" cy="0" r="2" fill={i === 3 ? a.c : '#1a1a1a'} stroke={a.c} strokeWidth="0.6" filter="url(#softBloom)">
                    {i === 3 && <animate attributeName="opacity" values="0.5;1;0.5" dur="1.4s" repeatCount="indefinite" />}
                  </circle>
                  <text x="9" y="3" fontFamily="JetBrains Mono, monospace" fontSize="5.5" fill="#525252" letterSpacing="1">
                    {a.lbl}
                  </text>
                </g>
              ))}
            </g>
            {/* live clock readout */}
            <text x="558" y="100" fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#737373" letterSpacing="1.5" textAnchor="end">
              SYS · OPERATIONAL · 90D
            </text>

            {/* BLADES BACKPLANE */}
            <rect x="78" y="108" width="484" height="240" fill="#020202" stroke="rgba(255,255,255,0.05)" />

            {/* vertical service blades */}
            {Array.from({ length: b.slots }).map((_, c) => {
              const blW = 484 / b.slots;
              const x = 78 + c * blW;
              return (
                <g key={`blade-${c}`}>
                  {/* blade body */}
                  <rect
                    x={x + 0.5}
                    y={114}
                    width={blW - 1}
                    height={228}
                    fill="#070707"
                    stroke="rgba(255,255,255,0.06)"
                  />
                  {/* handle */}
                  <rect
                    x={x + 1}
                    y={116}
                    width={blW - 2}
                    height={8}
                    fill="url(#metalHandle)"
                    stroke="rgba(255,255,255,0.12)"
                  />
                  {/* slot number */}
                  <text
                    x={x + blW / 2}
                    y={355}
                    fontFamily="JetBrains Mono, monospace"
                    fontSize="6"
                    fill="#525252"
                    letterSpacing="0.5"
                    textAnchor="middle"
                  >
                    {String(c + 1).padStart(2, '0')}
                  </text>
                </g>
              );
            })}

            {/* PORT GRID — small SFP/PON sockets */}
            {grid.cells.map((cell, i) => {
              const sw = grid.colW * 0.62;
              const sh = grid.rowH * 0.62;
              return (
                <g key={`p-${i}`}>
                  <rect
                    x={cell.x - sw / 2}
                    y={cell.y - sh / 2}
                    width={sw}
                    height={sh}
                    fill="#000"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="0.3"
                  />
                  {/* inner LED — randomly tinted */}
                  <circle
                    cx={cell.x}
                    cy={cell.y}
                    r={Math.min(sw, sh) * 0.18}
                    fill={
                      (i + cell.col) % 17 === 0
                        ? '#facc15'
                        : (i * 3 + cell.row) % 23 === 0
                          ? '#dc2626'
                          : 'var(--theme-primary)'
                    }
                    filter="url(#softBloom)"
                    opacity="0.45"
                  >
                    <animate
                      attributeName="opacity"
                      values={`${0.25 + ((i * 7) % 5) * 0.1};1;${0.3 + ((i * 11) % 5) * 0.1}`}
                      dur={`${1.4 + ((i * 13) % 20) * 0.12}s`}
                      repeatCount="indefinite"
                      begin={`${((i * 53) % 100) / 50}s`}
                    />
                  </circle>
                </g>
              );
            })}

            {/* SCAN BEAM — sweeps top to bottom across the blades */}
            <g opacity="0.7">
              <rect
                x="78"
                y="108"
                width="484"
                height="3"
                fill="var(--theme-primary)"
                filter="url(#hardBloom)"
              >
                <animate
                  attributeName="y"
                  values="108;348;108"
                  dur="6s"
                  repeatCount="indefinite"
                  keyTimes="0;0.5;1"
                  calcMode="spline"
                  keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
                />
                <animate
                  attributeName="opacity"
                  values="0;0.55;0"
                  dur="6s"
                  repeatCount="indefinite"
                  keyTimes="0;0.5;1"
                />
              </rect>
            </g>

            {/* bottom info strip */}
            <rect x="78" y="350" width="484" height="22" fill="#080808" stroke="rgba(255,255,255,0.06)" />
            <text x="84" y="364" fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#737373" letterSpacing="1.5">
              {`FAN OK · 32°C · ${b.serial} · CTRL-A:ACTIVE · CTRL-B:STANDBY`}
            </text>
            <text x="558" y="364" fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#737373" letterSpacing="1.5" textAnchor="end">
              {`OLT FLEET · ${olts.toLocaleString()}`}
            </text>
          </g>

          {/* DATA BEAMS — shoot from random PON ports into space (drawn outside the clip) */}
          {activePorts.map((p, i) => (
            <g key={`db-${b.id}-${i}`}>
              {/* trail path (faint) */}
              <use
                href={`#beam-${b.id}-${i}`}
                stroke="var(--theme-primary)"
                strokeOpacity="0.18"
                strokeWidth="0.8"
                fill="none"
                filter="url(#softBloom)"
              />
              {/* moving photon */}
              <circle r="3" fill="url(#ledGlow)" filter="url(#hardBloom)">
                <animateMotion
                  dur={`${p.dur}s`}
                  repeatCount="indefinite"
                  begin={`${p.delay}s`}
                  rotate="auto"
                >
                  <mpath href={`#beam-${b.id}-${i}`} />
                </animateMotion>
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  dur={`${p.dur}s`}
                  repeatCount="indefinite"
                  begin={`${p.delay}s`}
                  keyTimes="0;0.15;0.85;1"
                />
              </circle>
              {/* port flash at origin */}
              <circle cx={p.x} cy={p.y} r="3" fill="#fff" filter="url(#hardBloom)" opacity="0">
                <animate
                  attributeName="opacity"
                  values="0;1;0"
                  dur="0.6s"
                  begin={`${p.delay}s; ${p.delay}s + ${p.dur}s; ${p.delay}s + ${p.dur * 2}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="r"
                  values="1;5;1"
                  dur="0.6s"
                  begin={`${p.delay}s; ${p.delay}s + ${p.dur}s; ${p.delay}s + ${p.dur * 2}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          ))}

          {/* corner labels */}
          <g opacity="0.55">
            <text x="8" y="14" fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#525252" letterSpacing="2">
              ◆ LIVE TOPOLOGY · GPON
            </text>
            <text x="632" y="14" fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#525252" letterSpacing="2" textAnchor="end">
              CTRL · A:STANDBY · B:ACTIVE
            </text>
            <text x="8" y="394" fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#525252" letterSpacing="2">
              SNMP V3 · AUTHPRIV · TLS 1.3
            </text>
            <text x="632" y="394" fontFamily="JetBrains Mono, monospace" fontSize="6" fill="#525252" letterSpacing="2" textAnchor="end">
              {`RX -18 dBm · TX +3 dBm`}
            </text>
          </g>
        </svg>
      </div>
    </div>
  );
}
